<template>
  <v-card style="background: rgb(var(--v-theme-background))">
    <v-card-title>Login</v-card-title>
    <v-card-text>
      <v-form @submit.prevent="login">
        <v-text-field
          v-model="email"
          label="Email"
          type="email"
          required
          density="comfortable"
          variant="outlined"
          :rules="[(v) => !!v || 'Email is required']"
        ></v-text-field>

        <v-text-field
          v-model="password"
          label="Password"
          :type="showPassword ? 'text' : 'password'"
          required
          density="comfortable"
          variant="outlined"
          class="mt-2"
          :rules="[(v) => !!v || 'Password is required']"
          :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
          @click:append-inner="showPassword = !showPassword"
        ></v-text-field>

        <v-alert v-if="error" type="error" class="my-4">
          {{ error }}
        </v-alert>

        <!-- <v-alert v-if="message" type="success" class="my-4"> -->
        <!--   {{ message }} -->
        <!-- </v-alert> -->

        <v-btn type="submit" color="primary" variant="flat" block class="mb-2 mt-4" :loading="loading"> Login </v-btn>

        <div class="text-center mt-3">
          <router-link to="/forgot-password" class="text-primary text-decoration-none">
            Forgot Password?
          </router-link>
        </div>

        <!-- Admin credentials hint -->
        <!-- <v-alert type="info" class="mt-4" density="compact"> -->
        <!--   <div class="text-caption"> -->
        <!--     <strong>Demo Admin:</strong> admin@example.com / admin123<br> -->
        <!--     <strong>Note:</strong> User accounts must be created by administrator. -->
        <!--   </div> -->
        <!-- </v-alert> -->
      </v-form>

    </v-card-text>
  </v-card>
</template>

<script setup>
import { ref } from "vue";
import api, { startTokenRefreshTimer } from "@/utils/api.js";
import { useRouter, useRoute } from "vue-router";
import { useApp } from "@/composables/useApp.js";
import { useUserPreferences } from "@/composables/useUserPreferences.js";

const router = useRouter();
const route = useRoute();

const { user, isAuthenticated, userRole, isDev, currentApp } = useApp();
const { loadPreferences } = useUserPreferences();

// const emit = defineEmits(["login"]);

const email = ref(
  // import.meta.env.VITE_APP_NODE_ENV === "development" ? "maurizio+admin@pizzafelice.com" : null,
  isDev ? "maurizio+appadmin@pizzafelice.com" : null,
  // isDev ? "maurizio+admin@pizzafelice.com" : null,
);
const password = ref(isDev ? "Frescott8!" : null);
// const password = ref(isDev ? "Frullodifiond@1" : null);

const error = ref("");
const message = ref("");
const loading = ref(false);
const showPassword = ref(false);

// Helper to parse expiresIn string (e.g., "15m", "7d") to milliseconds
const parseExpiresIn = (expiresIn) => {
  const match = expiresIn.match(/^(\d+)([smhd])$/);
  if (!match) return 15 * 60 * 1000; // default 15 minutes
  const value = parseInt(match[1]);
  const unit = match[2];
  switch (unit) {
    case "s": return value * 1000;
    case "m": return value * 60 * 1000;
    case "h": return value * 60 * 60 * 1000;
    case "d": return value * 24 * 60 * 60 * 1000;
    default: return 15 * 60 * 1000;
  }
};

const login = async () => {
  loading.value = true;
  error.value = "";
  message.value = "";

  try {
    // Login via auth server
    const response = await api.authApi.post("/api/auth/login", {
      email: email.value,
      password: password.value,
    });

    isAuthenticated.value = true;

    user.value = response.data.user;
    localStorage.setItem("user", JSON.stringify(response.data.user));

    userRole.value = response.data.user.role;

    // Store JWT token and user info
    localStorage.setItem("authToken", response.data.token);
    localStorage.setItem("userRole", user.value.role);

    // Calculate and store token expiry based on server response
    const expiresIn = response.data.expiresIn || "15m";
    const expiryMs = parseExpiresIn(expiresIn);
    const expiry = new Date(Date.now() + expiryMs);
    localStorage.setItem("tokenExpiry", expiry.toISOString());

    // Check if password change is required
    if (response.data.passwordChangeRequired) {
      localStorage.setItem("passwordChangeRequired", "true");
      router.push("/force-password-change");
      return;
    }

    // Clear any stale password change flag
    localStorage.removeItem("passwordChangeRequired");

    // Start auto-refresh timer and load user preferences from server
    startTokenRefreshTimer();
    loadPreferences();

    // Determine which app to navigate to
    const _currentApp = JSON.parse(localStorage.getItem("currentApp"));
    const apps = user.value.allowed_apps;
    const allowed_apps = Array.isArray(apps) ? apps : JSON.parse(apps || "[]");

    // Use stored currentApp if valid, otherwise pick first available app
    if (_currentApp && allowed_apps.includes(_currentApp)) {
      currentApp.value = _currentApp;
    } else if (allowed_apps.includes("sales")) {
      currentApp.value = "sales";
    } else if (allowed_apps.includes("reviews")) {
      currentApp.value = "reviews";
    } else if (allowed_apps.includes("assets")) {
      currentApp.value = "assets";
    } else if (allowed_apps.length > 0) {
      currentApp.value = allowed_apps[0];
    } else {
      currentApp.value = "sales"; // fallback
    }
    localStorage.setItem("currentApp", JSON.stringify(currentApp.value));

    // If login was triggered by a ?redirect= param (e.g. from /docs/), navigate
    // there using window.location so server-side routes (docs, slides) work.
    // Append ?token= so the Koa JWT middleware can authenticate the request.
    if (route.query.redirect) {
      const redirectPath = decodeURIComponent(route.query.redirect);
      const token = response.data.token;
      const sep = redirectPath.includes("?") ? "&" : "?";
      window.location.href = redirectPath + sep + "token=" + token;
      return;
    }

    // Navigate to last visited page or home
    const lastPath = localStorage.getItem("lastPath");
    if (lastPath) {
      localStorage.removeItem("lastPath");
      router.push(lastPath);
    } else {
      router.push("/home");
    }
  } catch (err) {
    error.value = err.response?.data?.error || "Login failed";
  } finally {
    loading.value = false;
  }
};
</script>
